import axios from 'axios';

const DEFAULT_TIMEOUT_MS = 60000;
const REPORT_TIMEOUT_MS = 900000;

const getDefaultApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://127.0.0.1:9090/api';
    }
  }
  return '/api';
};

const API_BASE_URL = process.env.REACT_APP_API_URL || getDefaultApiBaseUrl();

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // Health check
  async healthCheck() {
    const response = await apiClient.get('/health');
    return response.data;
  },

  // EDR Providers CRUD
  async getEDRProviders() {
    const response = await apiClient.get('/edr-providers');
    return response.data.data || [];
  },

  async getEDRProvider(id) {
    const response = await apiClient.get(`/edr-providers/${id}`);
    return response.data.data;
  },

  async createEDRProvider(providerData) {
    const response = await apiClient.post('/edr-providers', providerData);
    return response.data.data;
  },

  async updateEDRProvider(id, providerData) {
    const response = await apiClient.put(`/edr-providers/${id}`, providerData);
    return response.data.data;
  },

  async deleteEDRProvider(id) {
    const response = await apiClient.delete(`/edr-providers/${id}`);
    return response.data;
  },

  // EDR Alerts and Endpoints
  async getEDRAlerts() {
    const response = await apiClient.get('/edr/alerts');
    return response.data.data || [];
  },

  async getEDREndpoints() {
    const response = await apiClient.get('/edr/endpoints');
    return response.data.data || [];
  },

  async generateReport(reportData) {
    const response = await apiClient.post('/report', reportData);
    return {
      data: response.data.data || response.data,
      engine: response.data.engine || response.headers['x-report-engine'] || 'system',
      notice: response.data.notice || response.headers['x-report-engine-notice'] || '',
    };
  },

  async downloadReport(reportData, format, filenameBase = 'report') {
    const response = await apiClient.post('/report', reportData, {
      responseType: 'blob',
      timeout: REPORT_TIMEOUT_MS,
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const safeBaseName = String(filenameBase || 'report')
      .trim()
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
      .replace(/\s+/g, '_');
    const extension = format === 'pdf_docx' ? 'zip' : format;
    link.setAttribute('download', `${safeBaseName || 'report'}.${extension}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return {
      engine: response.headers['x-report-engine'] || 'system',
      notice: response.headers['x-report-engine-notice'] || '',
    };
  },

  async downloadReportTaskPdf(reportData, filenameBase = 'report') {
    const response = await apiClient.post('/report-task', reportData, {
      responseType: 'blob',
      timeout: REPORT_TIMEOUT_MS,
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const safeBaseName = String(filenameBase || 'report')
      .trim()
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
      .replace(/\s+/g, '_');
    link.setAttribute('download', `${safeBaseName || 'report'}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return {
      engine: response.headers['x-report-engine'] || 'system',
      notice: response.headers['x-report-engine-notice'] || '',
    };
  },

  // Languages
  async getLanguages() {
    const response = await apiClient.get('/languages');
    return response.data;
  },

  // Seed data
  async loadSeedData() {
    const response = await apiClient.post('/seed');
    return response.data;
  },

  // EDR Configuration
  async getEDRConfig() {
    const response = await apiClient.get('/edr/config');
    return response.data;
  },

  async saveEDRConfig(config) {
    const response = await apiClient.post('/edr/config', config);
    return response.data;
  },

  async getEDRStatus() {
    const response = await apiClient.get('/edr/status');
    return response.data;
  },

  async getEDRDashboard() {
    const response = await apiClient.get('/edr/dashboard');
    return response.data.data || {};
  },

  async getEDRReportData(params) {
    const response = await apiClient.get('/edr/report-data', {
      params,
      timeout: REPORT_TIMEOUT_MS,
    });
    return response.data.data || {};
  },

  async getAISettings() {
    const response = await apiClient.get('/admin/ai-settings');
    return response.data.data || {};
  },

  async saveAISettings(settings) {
    const response = await apiClient.post('/admin/ai-settings', settings);
    return response.data.data || {};
  },

  async testEDRConnection(provider, config) {
    const response = await apiClient.post('/edr/test', {
      provider,
      enabled: true,
      ...config,
    });
    return response.data.data;
  },

  // Hierarchy data
  async getEDRHierarchy() {
    try {
      console.log('Fetching EDR hierarchy...');
      const response = await apiClient.get('/edr/hierarchy');
      console.log('Hierarchy response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching hierarchy:', error);
      throw error;
    }
  },

  // Reports (updated for EDR)
  async getReports() {
    const response = await apiClient.get('/reports');
    return response.data.data || [];
  },
};

export default apiService;
export const api = apiService;
